import {
    ChangeEvent,
    createContext,
    FC,
    FormEvent,
    FormHTMLAttributes,
    MouseEventHandler,
    ReactNode,
    useRef,
    useState,
} from 'react';
import { trackFormSubmit } from '~/analytics';
import { ApiError, type ApiV2ErrorShape, type ApiV2ServerErrorShape } from '~/api.ts';
import type { ButtonGroupProps, ButtonProps } from '~/components/new/Button';
import Button, { ButtonGroup, ButtonLink } from '~/components/new/Button';
import { ChipTag } from '~/components/new/Chip';
import { ErrorAlert } from '~/components/ui/Alert';
import CheckBox from '~/components/ui/forms/CheckBox';
import Choice, { YesOrNo } from '~/components/ui/forms/Choice';
import DatePicker from '~/components/ui/forms/DatePicker';
import {
    ChoiceField,
    DatePickerField,
    Field,
    FieldError,
    FieldHelp,
    FieldInfo,
    FieldLabel,
    InputField,
    SelectField,
    SwitchField,
    TextareaField,
} from '~/components/ui/forms/Fields';
import Input, { ArrayInput, PhoneInput } from '~/components/ui/forms/Input';
import Select, { YearSelector } from '~/components/ui/forms/Select';
import Textarea from '~/components/ui/forms/Textarea';
import { deepMerge, get, set } from '~/utils/object';
import { classNames } from '~/utils/styles';
import type {
    DeepPartial,
    FlattenedPathValues,
    FlattenObjectKeys,
    ValuesToBool,
} from '~/utils/typescript';

// Export for backwards compatibility.
export {
    ArrayInput,
    CheckBox,
    Choice,
    DatePicker,
    Input,
    PhoneInput,
    Select,
    Textarea,
    YearSelector,
    YesOrNo,
};

export { ChoiceField, DatePickerField, InputField, SelectField, SwitchField, TextareaField };

export type FormChanged<T, TStop extends keyof any = never> = ValuesToBool<T, TStop>;
export type FormErrors<T, TStop extends keyof any = never> = ValuesToBool<T, TStop>;

export type ValidationFunction<T, E = FormErrors<T>> = (data: T) => E;

/** Zod-like schema: object with safeParse returning success/error.issues */
type ZodLikeSchema = {
    safeParse: (
        data: unknown,
    ) =>
        | { success: true }
        | { success: false; error: { issues: Array<{ path: (string | number | symbol)[] }> } };
};

function zodErrorsToFormErrors(
    issues: { path: (string | number | symbol)[] }[],
): Record<string, unknown> {
    const errors: Record<string, unknown> = {};
    for (const issue of issues) {
        const path = issue.path.filter((p): p is string | number => typeof p !== 'symbol');
        const field = path.join('.');
        set(errors, field, true);
    }
    return errors;
}

/** Server validation error: array of { path: string[] } or { location: string } (e.g. from 422 response) */
function parseServerValidationErrors(err: unknown): Record<string, unknown> | null {
    const raw = Array.isArray(err)
        ? err
        : ((err as { error?: unknown })?.error ??
          (err as { errors?: unknown[] })?.errors ??
          (err as { detail?: unknown[] })?.detail ??
          (err as { issues?: unknown[] })?.issues);
    const issues = Array.isArray(raw) ? raw : null;
    if (!issues) return null;
    const errors: Record<string, unknown> = {};
    for (const issue of issues) {
        const i = issue as { path?: (string | number)[]; location?: string };
        let field: string | null = null;
        if (Array.isArray(i.path)) {
            const path = i.path[0] === 'body' ? i.path.slice(1) : i.path;
            if (path.length > 0) field = path.map(String).join('.');
        } else if (typeof i.location === 'string') {
            // Huma/OpenAPI: "body.address.city" -> "address.city"
            const loc = i.location.replace(/^body\./, '');
            if (loc) field = loc;
        }
        if (field) set(errors, field, true);
    }
    return Object.keys(errors).length > 0 ? errors : null;
}

function collectErrorPaths(obj: Record<string, unknown>, prefix = ''): Array<string> {
    const paths: string[] = [];
    for (const [key, val] of Object.entries(obj)) {
        const path = prefix ? `${prefix}.${key}` : key;
        if (Array.isArray(val)) {
            val.forEach((item, i) => {
                const itemPath = `${path}[${i}]`;
                if (item && typeof item === 'object' && !Array.isArray(item)) {
                    paths.push(...collectErrorPaths(item as Record<string, unknown>, itemPath));
                } else if (item) {
                    paths.push(itemPath);
                }
            });
        } else if (val && typeof val === 'object') {
            paths.push(...collectErrorPaths(val as Record<string, unknown>, path));
        } else if (val) {
            paths.push(path);
        }
    }
    return paths;
}

function getApiV2FieldErrors(error: ApiError | null): ApiV2ErrorShape | null {
    if (!error) return null;
    if (Array.isArray(error)) return error as ApiV2ErrorShape;
    if (
        typeof error === 'object' &&
        'errors' in error &&
        Array.isArray((error as ApiV2ServerErrorShape).errors)
    ) {
        return (error as ApiV2ServerErrorShape).errors ?? null;
    }
    return null;
}

function mergeErrorObjects(
    a: Record<string, unknown>,
    b: Record<string, unknown>,
): Record<string, unknown> {
    const result = { ...a };
    for (const path of collectErrorPaths(b)) {
        set(result, path, true);
    }
    return result;
}

function createZodValidator<T>(
    schema: ZodLikeSchema,
): ValidationFunction<T, Record<string, unknown>> {
    return (data: T) => {
        const result = schema.safeParse(data);
        if (result.success) return {};
        return zodErrorsToFormErrors(result.error.issues);
    };
}

export type Form<T, E = FormErrors<T>> = {
    submitting: boolean;
    error: ApiError | null;
    errors: E;
    hasErrors: boolean;
    hasError: (error: FlattenObjectKeys<T>, field?: string) => boolean;
    getError: (error: FlattenObjectKeys<T>, field?: string) => string | undefined;
    canSubmit: boolean;
    changed: FormChanged<T>;
    hasChanges: boolean;
    hasChange: (field: FlattenObjectKeys<T>) => boolean;
    setCurrentAsInitial: () => void;
    data: T;
    handleChange: (field: FlattenObjectKeys<T>, value: any) => void;
    handleInputChange: (
        field: FlattenObjectKeys<T>,
        asNumber?: boolean,
    ) => (e: ChangeEvent<HTMLInputElement>) => void;
    setFields: (newData: DeepPartial<T>, silent?: boolean) => void;
    getField: <P extends FlattenObjectKeys<T>>(name: P) => FlattenedPathValues<T, P>;
    submit: <R>(
        func: SubmitFunction<T, R>,
        onSuccess?: (resp: R) => void,
        onError?: (err: ApiError) => void,
    ) => Promise<void>;
    reset: () => void;
    setError: (error: ApiError | null) => void;
    Error: FC<FormErrorProps>;
    Cancel: FC<FormCancelProps>;
    Submit: FC<FormSubmitProps>;
    formRef?: React.RefObject<HTMLFormElement | null>;
};

type FormErrorProps = { detailed?: boolean; children: ReactNode };
type FormCancelProps = {
    to?: string;
    text?: string;
    onClick?: MouseEventHandler<HTMLButtonElement>;
} & Pick<ButtonProps, 'variant' | 'action'>;
type FormSubmitProps = {
    disabled?: boolean | (() => boolean);
    text?: string;
    className?: string;
    'data-test-name'?: string;
} & Pick<ButtonProps, 'variant' | 'action' | 'icon'>;

export type SubmitFunction<T, R> = (data: T) => Promise<R>;
export enum ValidationMethod {
    Preventive = 'preventive',
    Reactive = 'reactive',
}
export type ValidationMethodType = (typeof ValidationMethod)[keyof typeof ValidationMethod];

export type FormValidate<T, E = FormErrors<T>> = ValidationFunction<T, E> | ZodLikeSchema;

export function useForm<T extends Record<string, any>, E = FormErrors<T>>(
    initialData: T,
    validate: FormValidate<T, E> = () => ({}) as E,
    method: ValidationMethodType = ValidationMethod.Preventive,
): Form<T, E> {
    const validateFn: ValidationFunction<T, E> =
        typeof validate === 'function'
            ? validate
            : (createZodValidator(validate) as ValidationFunction<T, E>);

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<ApiError | null>(null);
    const [serverValidationErrors, setServerValidationErrors] = useState<Record<
        string,
        unknown
    > | null>(null);
    const [changed, setChanged] = useState<FormChanged<T>>({});
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [data, setData] = useState<T>(initialData);

    const validatorErrors =
        method === ValidationMethod.Preventive
            ? (validateFn(data) as Record<string, unknown>)
            : ((error || {}) as Record<string, unknown>);
    const errors = (
        serverValidationErrors
            ? mergeErrorObjects(validatorErrors, serverValidationErrors)
            : validatorErrors
    ) as E;
    const hasErrors = checkErrors(errors);

    const handleChange = (field: FlattenObjectKeys<T>, value: any) => {
        setServerValidationErrors(null);
        if (!hasChanges) {
            setHasChanges(true);
        }
        setChanged((changed) => {
            const newChanged = { ...changed };
            set(newChanged, field, typeof value === 'object' ? setTrueRecursively(value) : true);

            return newChanged;
        });
        setData((data) => {
            const newData = { ...data };
            set(newData, field, value);
            return newData;
        });
    };

    const handleInputChange =
        (field: FlattenObjectKeys<T>, asNumber = false) =>
        (e: ChangeEvent<HTMLInputElement>) => {
            if (asNumber) {
                handleChange(
                    field,
                    // when not valid, set to undefined
                    isNaN(parseFloat(e.target.value)) ? undefined : parseFloat(e.target.value),
                );
            } else {
                handleChange(field, e.target.value);
            }
        };

    const setFields = (newData: DeepPartial<T>, silent = false) => {
        if (!silent) {
            setHasChanges(true);

            const newChanged = setTrueRecursively(newData);

            setChanged((changed) => deepMerge(changed, newChanged));
        }
        setData((data) => {
            const next = { ...data };
            for (const [key, value] of Object.entries(newData)) {
                if (key.includes('.') || key.includes('[')) {
                    set(next, key, value);
                } else if (isPlainObject(value)) {
                    (next as Record<string, any>)[key] = deepMerge(
                        (data as Record<string, any>)[key] ?? {},
                        value,
                    );
                } else {
                    (next as Record<string, any>)[key] = value;
                }
            }
            return next;
        });
    };

    const getField = (field: FlattenObjectKeys<T>) => get(data, field);

    function submit<R>(
        func: SubmitFunction<T, R>,
        onSuccess: (resp: R) => void = () => {},
        onError: (err: any) => void = () => {},
    ): Promise<void> {
        let _hasErrors = hasErrors; // upper scope initial value
        if (method === ValidationMethod.Reactive) {
            // error check after submit clicked
            const errors = validateFn(data);
            _hasErrors = checkErrors(errors);

            if (_hasErrors) {
                // flag changes on errored fields
                setChanged((changed) =>
                    deepMerge(changed, setTrueRecursively(errors as Record<string, any>)),
                );
                // trigger re-render and error display
                setFields(data as DeepPartial<T>);
                setError(errors as ApiError);
                onError(errors);
                return Promise.reject();
            }
        }

        if (submitting || _hasErrors) return Promise.resolve();
        setSubmitting(true);
        setError(null);
        setServerValidationErrors(null);
        return func(data)
            .then((resp) => {
                onSuccess(resp);
            })
            .catch((err: ApiError | ApiV2ServerErrorShape) => {
                if (
                    err &&
                    typeof err === 'object' &&
                    'errors' in err &&
                    Array.isArray(err.errors)
                ) {
                    setError(err.errors);
                    onError(err.errors);
                } else {
                    setError(err as ApiError);
                    onError(err);
                }
                const serverErrors = parseServerValidationErrors(err);
                if (serverErrors) {
                    setServerValidationErrors(serverErrors);
                    setChanged((c) => {
                        const next = { ...c } as Record<string, unknown>;
                        for (const path of collectErrorPaths(serverErrors)) {
                            set(next, path, true);
                        }
                        return next as FormChanged<T>;
                    });
                }
            })
            .finally(() => setSubmitting(false));
    }

    const hasError = (error: FlattenObjectKeys<T>, field?: string): boolean => {
        if (!field) field = error;
        const hasServerError = Boolean(
            serverValidationErrors && get(serverValidationErrors, field),
        );
        const hasErrorForField =
            Boolean(get(errors as Record<string, any>, error as string)) || hasServerError;
        const isFieldRelevant =
            method === ValidationMethod.Preventive
                ? Boolean(get(changed, field)) ||
                  Boolean(get(initialData as Record<string, any>, field)) ||
                  hasServerError
                : Boolean(get(changed, field)) || hasServerError;

        return hasErrorForField && isFieldRelevant;
    };

    const getError = (field: FlattenObjectKeys<T>): string | undefined => {
        if (!hasError(field)) {
            return undefined;
        }
        const apiErrors = getApiV2FieldErrors(error);
        if (!apiErrors) {
            return undefined;
        }
        const bodyPath = `body.${field}`;
        return apiErrors.find((e) => e.location === bodyPath)?.message;
    };

    const reset = () => {
        setData(initialData);
        setHasChanges(false);
        setChanged({});
        setServerValidationErrors(null);
    };

    const setCurrentAsInitial = () => {
        // Reassigns the hook argument so reset() uses the new baseline. Only holds for the
        // current render; fixing it means storing the baseline in a ref, which changes
        // reset() behaviour for every legacy form. See #7032.
        // eslint-disable-next-line react-hooks/immutability
        initialData = data;
        setHasChanges(false);
        setChanged({});
    };

    const hasChange = (field: FlattenObjectKeys<T>): boolean => {
        return Boolean(get(changed, field));
    };
    const formRef = useRef<HTMLFormElement>(null);

    return {
        submitting,
        error,
        errors,
        hasErrors,
        hasError,
        getError,
        canSubmit: !submitting && !hasErrors,
        changed,
        hasChanges,
        hasChange,
        setCurrentAsInitial,
        data,
        handleChange,
        handleInputChange,
        setFields,
        getField,
        submit,
        reset,
        setError,
        Error: ({ detailed = true, children }: FormErrorProps) =>
            error && <ErrorAlert errors={detailed ? error : undefined}>{children}</ErrorAlert>,
        Cancel: ({
            to,
            text = 'Cancel',
            variant = 'floating',
            action = 'primary',
            onClick,
        }: FormCancelProps) =>
            to ? (
                <ButtonLink
                    to={to}
                    variant={variant}
                    action={action}
                    disabled={submitting}
                    data-action='cancel'
                    className='no-underline'>
                    {text}
                </ButtonLink>
            ) : (
                <Button
                    onClick={onClick}
                    variant={variant}
                    action={action}
                    disabled={submitting}
                    data-action='cancel'>
                    {text}
                </Button>
            ),
        Submit: ({
            disabled,
            text = 'Save changes',
            variant = 'filled',
            action = 'primary',
            className = '',
            icon,
            'data-test-name': testName = 'submit',
        }: FormSubmitProps) => (
            <Button
                type={formRef.current ? 'button' : 'submit'} // fallback if useForm is not into <Form> context
                variant={variant}
                action={action}
                icon={icon}
                className={className}
                processing={submitting}
                onClick={() => {
                    if (formRef.current) {
                        formRef.current.requestSubmit();
                    }
                }}
                disabled={
                    submitting ||
                    (method === ValidationMethod.Preventive && hasErrors) ||
                    (typeof disabled === 'function' ? disabled() : disabled)
                }
                data-test-component='ui/Form/Submit'
                data-test-name={testName}>
                {text}
            </Button>
        ),
        formRef,
    };
}

const isPlainObject = (val: unknown): val is Record<string, any> => {
    if (val === null || typeof val !== 'object' || Array.isArray(val)) {
        return false;
    }
    const proto = Object.getPrototypeOf(val);
    return proto === Object.prototype || proto === null;
};

const setTrueRecursively = (val: any): any => {
    if (Array.isArray(val)) {
        return val.map(setTrueRecursively);
    } else if (val && typeof val === 'object') {
        const result: Record<string, any> = {};
        const valObj = val as Record<string, any>;
        for (const key of Object.keys(valObj)) {
            result[key] = setTrueRecursively(valObj[key]);
        }
        return result;
    } else {
        return true;
    }
};

function checkErrors<E>(errors: E): boolean {
    return Object.values(errors as Record<string, any>).some((value) => {
        if (typeof value === 'object') {
            return checkErrors(value);
        }
        return Boolean(value);
    });
}

export const formContext = createContext<Form<any> | null>(null);

export type FormProps = FormHTMLAttributes<HTMLFormElement> & {
    children: ReactNode;
    onSubmit: (e: FormEvent<HTMLFormElement>) => void;
    form?: Form<any>;
};

function Form({ children, onSubmit, form, ...props }: FormProps) {
    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.stopPropagation();
        e.preventDefault();

        trackFormSubmit(props.id || '', props.name);
        onSubmit(e);
    };

    return (
        <form
            {...props}
            onSubmit={handleSubmit}
            ref={form?.formRef}>
            <formContext.Provider value={form || null}>{children}</formContext.Provider>
        </form>
    );
}

type FormGroupProps = {
    children: ReactNode;
    className?: string;
};

function FormGroup({ children, className }: FormGroupProps) {
    return (
        <div className={classNames({ 'form-group': true, [className || '']: Boolean(className) })}>
            {children}
        </div>
    );
}

function FormSection({ children, className = '' }: { children: ReactNode; className?: string }) {
    return <div className={`form-section ${className}`}>{children}</div>;
}

function FormOptional() {
    return <span className='optional'>(Optional)</span>;
}

type FormButtonsProps = Omit<ButtonGroupProps, 'align'>;

function FormButtons({ children, ...props }: FormButtonsProps) {
    return (
        <ButtonGroup
            align='sides'
            {...props}>
            {children}
        </ButtonGroup>
    );
}

type FormHeaderProps = {
    tag?: string;
    title: ReactNode;
    description?: ReactNode;
    children?: ReactNode;
};

function FormHeader({ tag, title, description, children }: FormHeaderProps) {
    return (
        <div className='flex flex-column items-center gap-4 w-full'>
            {tag && <ChipTag color='gradient'>{tag}</ChipTag>}
            <div className='flex flex-column justify-center items-center gap-2 w-full'>
                <div className='desktop-title-1 m-0 mt-1 text-center hide-empty'>{title}</div>
                <span className='desktop-body-1-regular text-center hide-empty'>{description}</span>
                {children}
            </div>
        </div>
    );
}

// Extend Form with subcomponents
type FormSubComponents = {
    /**
     * @deprecated: use `FormLayout.Group` instead
     */
    Group: typeof FormGroup;
    /**
     * @deprecated: use a field form Fields.tsx (or add it there) instead
     */
    Field: typeof Field;
    /**
     * @deprecated: use a Label instead
     */
    Label: typeof FieldLabel;
    /**
     * @deprecated: use a field form Fields.tsx and set the error
     */
    Error: typeof FieldError;
    /**
     * @deprecated: use a field form Fields.tsx and set the help
     */
    Help: typeof FieldHelp;
    /**
     * @deprecated: use a field form Fields.tsx and set the info
     */
    Info: typeof FieldInfo;
    /**
     * @deprecated: use `FormLayout.Group` instead
     */
    Section: typeof FormSection;
    /**
     * @deprecated: IDK what this is but don't use it.
     */
    Optional: typeof FormOptional;
    /**
     * @deprecated: use `FormLayout.Buttons` instead
     */
    Buttons: typeof FormButtons;
    /**
     * @deprecated: use `FormLayout.Header` instead
     */
    Header: typeof FormHeader;
};

(Form as React.FC<FormProps> & FormSubComponents).Group = FormGroup;
(Form as React.FC<FormProps> & FormSubComponents).Field = Field;
(Form as React.FC<FormProps> & FormSubComponents).Label = FieldLabel;
(Form as React.FC<FormProps> & FormSubComponents).Error = FieldError;
(Form as React.FC<FormProps> & FormSubComponents).Help = FieldHelp;
(Form as React.FC<FormProps> & FormSubComponents).Info = FieldInfo;
(Form as React.FC<FormProps> & FormSubComponents).Section = FormSection;
(Form as React.FC<FormProps> & FormSubComponents).Optional = FormOptional;
(Form as React.FC<FormProps> & FormSubComponents).Buttons = FormButtons;
(Form as React.FC<FormProps> & FormSubComponents).Header = FormHeader;

export default Form as React.FC<FormProps> & FormSubComponents;
